import { useState, useEffect } from "react";
import { FaUserClock, FaCalendarAlt, FaStickyNote } from "react-icons/fa";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import DatePicker from "../modal/datepicker";
import TimePicker from "../modal/timepicker";
import api from "../services/api";
import { AttendanceStatus, AttendanceStatusOptions } from "../../constants/constants";
import "./attendance.css";

const AttendanceModal = ({ existing, onSave, onClose }) => {
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.get("dropdowns/employees/").then((r) => {
      setEmployees(r.data.data || r.data || []);
    }).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    employee: "",
    date: new Date().toISOString().split("T")[0],
    check_in_time: "",
    check_out_time: "",
    status: AttendanceStatus.PRESENT,
    remarks: "",
  });

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) {
      let empId = existing.employee || existing.employee_id || existing.employee_detail?.id || "";
      if (!empId && employees.length > 0) {
        const found = employees.find(
          (e) =>
            (existing.emp_id && e.emp_id === existing.emp_id) ||
            (existing.employee_name && (e.full_name || e.name || "").trim().toLowerCase() === existing.employee_name.trim().toLowerCase())
        );
        if (found) empId = found.id;
      }
      setForm((prev) => ({
        ...prev,
        employee: empId,
        date: existing.date || prev.date,
        check_in_time: existing.check_in_time || "",
        check_out_time: existing.check_out_time || "",
        status: existing.status || AttendanceStatus.PRESENT,
        remarks: existing.remarks || "",
      }));
    }
  }, [existing, employees]);

  const needsTime = [AttendanceStatus.PRESENT, AttendanceStatus.WORK_FROM_HOME, AttendanceStatus.HALF_DAY].includes(form.status);

  const handleSave = async () => {
    const errs = {};
    if (!form.employee) errs.employee = "Please select an employee";
    if (!form.date) errs.date = "Date is required";
    if (needsTime && !form.check_in_time) {
      errs.check_in_time = "Check-in time is required for this status";
    }
    if (needsTime && form.check_in_time && form.check_out_time && form.check_out_time < form.check_in_time) {
      errs.check_out_time = "Check-out time must be after check-in time";
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return setError("Please check the field errors below.");
    }
    setError("");
    setFieldErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        check_in_time: needsTime && form.check_in_time ? (form.check_in_time.length === 5 ? `${form.check_in_time}:00` : form.check_in_time) : null,
        check_out_time: needsTime && form.check_out_time ? (form.check_out_time.length === 5 ? `${form.check_out_time}:00` : form.check_out_time) : null,
      };
      await onSave(payload);
    } catch (err) {
      const respData = err?.response?.data;
      const backendErrors = respData?.errors || (respData && typeof respData === "object" && !respData.message && !respData.detail ? respData : null);
      if (backendErrors && typeof backendErrors === "object") {
        const formatted = {};
        for (const key in backendErrors) {
          const val = backendErrors[key];
          formatted[key] = Array.isArray(val) ? val[0] : val;
        }
        setFieldErrors(formatted);
      }
      setError(err?.response?.data?.message || err?.response?.data?.detail || "Please check the field errors below.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={existing ? "Edit Attendance" : "Mark Attendance"}
      onClose={onClose}
      size="lg"
    >
      {error && <div className="form-error">{error}</div>}

      <div className="attendance-form-body">

        {/* EMPLOYEE — single full-width field */}
        <div className="form-section-card">
          <div className="section-title-premium"><FaUserClock /> Employee Details</div>
          <div className="full-row">
            <Dropdown
              options={employees.map((e) => ({ label: `${e.full_name || e.name} (${e.emp_id})`, value: e.id }))}
              value={form.employee}
              placeholder="Select Employee"
              onChange={(val) => { setForm({ ...form, employee: val }); if (fieldErrors.employee) setFieldErrors({ ...fieldErrors, employee: null }); }}
            />
            {fieldErrors.employee && <div className="field-error">{fieldErrors.employee}</div>}
          </div>
        </div>

        {/* ATTENDANCE DETAILS */}
        <div className="form-section-card">
          <div className="section-title-premium"><FaCalendarAlt /> Attendance Details</div>
          <div className="grid-3">
            <div>
              <label>Date</label>
              <DatePicker
                className={fieldErrors.date ? "input-error" : ""}
                value={form.date}
                maxDate={new Date().toISOString().split("T")[0]}
                onChange={(val) => { setForm({ ...form, date: val }); if (fieldErrors.date) setFieldErrors({ ...fieldErrors, date: null }); }}
              />
              {fieldErrors.date && <div className="field-error">{fieldErrors.date}</div>}
            </div>
            <div>
              <label>Status</label>
              <Dropdown
                options={AttendanceStatusOptions}
                value={form.status}
                onChange={(val) => {
                  const updatedForm = { ...form, status: val };
                  // Clear check-in/check-out when switching to absent or leave
                  if ([AttendanceStatus.ABSENT, AttendanceStatus.ON_LEAVE, AttendanceStatus.HOLIDAY, AttendanceStatus.WEEKEND].includes(val)) {
                    updatedForm.check_in_time = "";
                    updatedForm.check_out_time = "";
                  }
                  setForm(updatedForm);
                  if (fieldErrors.status) setFieldErrors({ ...fieldErrors, status: null });
                }}
                placeholder="Status"
              />
              {fieldErrors.status && <div className="field-error">{fieldErrors.status}</div>}
            </div>
            {needsTime && (
              <div>
                <label>Check-In Time</label>
                <TimePicker
                  className={fieldErrors.check_in_time ? "input-error" : ""}
                  value={form.check_in_time}
                  onChange={(val) => { setForm({ ...form, check_in_time: val }); if (fieldErrors.check_in_time) setFieldErrors({ ...fieldErrors, check_in_time: null }); }}
                />
                {fieldErrors.check_in_time && <div className="field-error">{fieldErrors.check_in_time}</div>}
              </div>
            )}
            {needsTime && (
              <div>
                <label>Check-Out Time</label>
                <TimePicker
                  className={fieldErrors.check_out_time ? "input-error" : ""}
                  value={form.check_out_time}
                  onChange={(val) => { setForm({ ...form, check_out_time: val }); if (fieldErrors.check_out_time) setFieldErrors({ ...fieldErrors, check_out_time: null }); }}
                />
                {fieldErrors.check_out_time && <div className="field-error">{fieldErrors.check_out_time}</div>}
              </div>
            )}
          </div>
        </div>

        {/* ADDITIONAL */}
        <div className="form-section-card">
          <div className="section-title-premium"><FaStickyNote /> Additional</div>
          <div>
            <textarea
              className={fieldErrors.remarks ? "input-error" : ""}
              placeholder="Remarks"
              value={form.remarks}
              onChange={(e) => { setForm({ ...form, remarks: e.target.value }); if (fieldErrors.remarks) setFieldErrors({ ...fieldErrors, remarks: null }); }}
            />
            {fieldErrors.remarks && <div className="field-error">{fieldErrors.remarks}</div>}
          </div>
        </div>

      </div>

      <div className="modal-footer" >
        <div className="modal-actions">
          <button className="primary" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
          <button className="secondary" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </Modal>
  );
};

export default AttendanceModal;